import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetMatkaMarketDetailQuery } from '../services/matka/matkaApi';

const TABS = [
  { key: 'jodi',  label: 'Single Jodi' },
  { key: 'andar', label: 'Harup Andar' },
  { key: 'bahar', label: 'Harup Bahar' },
];

const Cell = ({ item }) => {
  const hasBets = (item.bets ?? 0) > 0;
  const pl = item.pl ?? 0;
  return (
    <div className={`border rounded overflow-hidden text-center text-xs ${hasBets ? 'border-blue-300 shadow-sm' : 'border-gray-200'}`}>
      <div className={`py-1.5 font-bold text-sm border-b ${hasBets ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
        {item.number}
      </div>
      <div className={`py-1.5 font-semibold ${pl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
        {Number(pl).toFixed(2)}
      </div>
    </div>
  );
};

const MatkaDetailPage = () => {
  const { matchId } = useParams();
  const navigate    = useNavigate();
  const [activeTab, setActiveTab] = useState('jodi');

  const { data, isLoading, error, refetch } = useGetMatkaMarketDetailQuery(matchId, { skip: !matchId });

  const summary        = data?.summary        || {};
  const jodiGrid       = [...(data?.jodiGrid       || [])].sort((a, b) => parseInt(a.number) - parseInt(b.number));
  const harufAndarGrid = [...(data?.harufAndarGrid  || [])].sort((a, b) => parseInt(a.number) - parseInt(b.number));
  const harufBaharGrid = [...(data?.harufBaharGrid  || [])].sort((a, b) => parseInt(a.number) - parseInt(b.number));

  const marketName = matchId?.split('-').slice(3).join(' ') || matchId || '';

  const BestWorst = ({ label, best, worst }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex-1 min-w-[180px]">
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-2">
        <span className="text-white text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex divide-x divide-gray-100">
        <div className="flex-1 px-4 py-3">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>
            <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Max Profit</span>
          </div>
          <div className="text-base font-bold text-gray-800">{best?.number ?? '-'}</div>
          <div className="text-sm font-semibold text-green-600">{best?.pl != null ? Number(best.pl).toFixed(2) : '-'}</div>
        </div>
        <div className="flex-1 px-4 py-3">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block"></span>
            <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Max Loss</span>
          </div>
          <div className="text-base font-bold text-gray-800">{worst?.number ?? '-'}</div>
          <div className="text-sm font-semibold text-red-500">{worst?.pl != null ? Number(worst.pl).toFixed(2) : '-'}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
        <span className="text-white font-semibold text-base capitalize">
          {marketName} &nbsp;·&nbsp; <span className="text-gray-400 text-sm font-normal">{matchId}</span>
        </span>
        <div className="flex gap-2">
          <button onClick={refetch} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 text-sm transition-colors">
            Refresh
          </button>
          <button onClick={() => navigate(-1)} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors">
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="m-4 p-3 bg-red-100 text-red-700 border border-red-300 text-sm">
          {error?.data?.message || 'Failed to load market data'}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Loading /></div>
      ) : (
        <>
          {/* Summary */}
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
            {/* Stats Row */}
            <div className="flex gap-3 mb-3">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-3 min-w-[130px]">
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Total Bets</div>
                <div className="text-2xl font-bold text-blue-600">{summary.totalBets ?? 0}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-3 min-w-[130px]">
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Total Stake</div>
                <div className="text-2xl font-bold text-indigo-600">{summary.totalStake ?? 0}</div>
              </div>
            </div>
            {/* Best/Worst Row */}
            <div className="flex flex-wrap gap-3">
              <BestWorst label="Jodi"  best={summary.jodiBestCase}  worst={summary.jodiWorstCase} />
              <BestWorst label="Andar" best={summary.andarBestCase} worst={summary.andarWorstCase} />
              <BestWorst label="Bahar" best={summary.baharBestCase} worst={summary.baharWorstCase} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-white px-5">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Jodi Grid — 10 columns */}
          {activeTab === 'jodi' && (
            <div className="p-5">
              <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                {jodiGrid.map((item) => (
                  <Cell key={item.number} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* Andar Grid */}
          {activeTab === 'andar' && (
            <div className="p-5">
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 max-w-2xl">
                {harufAndarGrid.map((item) => (
                  <Cell key={item.number} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* Bahar Grid */}
          {activeTab === 'bahar' && (
            <div className="p-5">
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 max-w-2xl">
                {harufBaharGrid.map((item) => (
                  <Cell key={item.number} item={item} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MatkaDetailPage;
